import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'
import { createColumnHelper } from '@tanstack/react-table'

import { IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Switch } from '@mui/material'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import LaunchIcon from '@mui/icons-material/Launch'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SettingsIcon from '@mui/icons-material/Settings'

import Table from '@/components/ui/tables/Table'

export type GroupData = {
  id: string
  name: string
  totalAccounts: number
  totalPolicies: number
}

export type GroupsTableProps = {
  data: GroupData[]
  loading?: boolean
  onDeleteClick?: (groupId: string) => void
  onDeleteManyClick?: (groupIds: string[]) => void
}

const columnHelper = createColumnHelper<GroupData>()

export function GroupsTable(props: GroupsTableProps) {
  const { data, loading, onDeleteClick, onDeleteManyClick } = props

  const navigate = useNavigate()
  const { t } = useTranslation()

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        enableHiding: true,
        header: t('table.groups.header.name').toString(),
      }),
      columnHelper.accessor('totalAccounts', {
        enableHiding: true,
        header: t('table.groups.header.totalAccounts').toString(),
      }),
      columnHelper.accessor('totalPolicies', {
        enableHiding: true,
        header: t('table.groups.header.totalPolicies').toString(),
      }),
      columnHelper.display({
        id: 'actions',
        header: ({ table }) => (
          <Stack alignItems="center" justifyContent="center">
            <PopupState variant="popover" popupId="groups-table-configuration">
              {(popupState) => (
                <>
                  <IconButton aria-label="configure columns" {...bindTrigger(popupState)}>
                    <SettingsIcon />
                  </IconButton>
                  <Menu
                    {...bindMenu(popupState)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <List>
                      {table
                        .getAllLeafColumns()
                        .filter((column) => column.id !== 'actions')
                        .map((column) => (
                          <ListItem key={column.id}>
                            <ListItemText id={column.id} primary={column.columnDef.header} />
                            <Switch edge="end" onChange={column.getToggleVisibilityHandler()} checked={column.getIsVisible()} />
                          </ListItem>
                        ))}
                    </List>
                  </Menu>
                </>
              )}
            </PopupState>
          </Stack>
        ),
        cell: ({ row, table }) => (
          <Stack justifyContent="flex-end" direction="row" spacing={1}>
            <PopupState variant="popover" popupId={`group-${row.id}-actions`}>
              {(popupState) => (
                <>
                  <IconButton aria-label="group actions" {...bindTrigger(popupState)} data-testid="group-menu-actions">
                    <MoreHorizIcon />
                  </IconButton>
                  <Menu
                    {...bindMenu(popupState)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      data-testid="open-group-details"
                      onClick={() => {
                        table.options.meta?.open?.(row.original.id)
                      }}
                    >
                      <ListItemIcon>
                        <LaunchIcon />
                      </ListItemIcon>
                      <ListItemText>{t('common:button.details.label')}</ListItemText>
                    </MenuItem>
                    <MenuItem
                      data-testid="delete-group"
                      onClick={() => {
                        table.options.meta?.delete?.(row.original.id)
                        popupState.close()
                      }}
                    >
                      <ListItemIcon>
                        <DeleteRoundedIcon />
                      </ListItemIcon>
                      <ListItemText>{t('common:button.delete.label')}</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>
          </Stack>
        ),
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ],
    [t]
  )

  return (
    <Table
      columns={columns}
      data={data}
      loading={loading}
      meta={{
        open: (groupId: string) => {
          navigate(`/admin/groups/${groupId}`)
        },
        delete: onDeleteClick,
      }}
      onDeleteMany={(rows) => {
        onDeleteManyClick?.(rows.map((row) => row.id))
      }}
      selectable
    />
  )
}

export default GroupsTable
